/**
 * B2C Order List Component
 * Version: 2.0.0
 * Last Updated: 2025-12-20
 *
 * Description:
 *   Database-first order management with independent WooCommerce sync
 *   
 * Changes in v2.0:
 *   - "Search Orders" now searches database only (instant)
 *   - New "Sync" button for WooCommerce sync (last 50 orders)
 *   - Status displayed as colored chips
 *   - Click order number to view full order details in popup
 *   - Auto-refresh after sync completes
 */

import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    Alert,
    CircularProgress,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
} from '@mui/material';
import {
    Search as SearchIcon,
    Download as DownloadIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    CheckCircle,
    Sync as SyncIcon,
    Close as CloseIcon,
    CalendarToday as CalendarTodayIcon,
    Print as PrintIcon,
    Inventory as InventoryIcon,
    CloudDone as CloudDoneIcon,
    CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { b2cOpsAPI } from '../../api';
import { formatDate } from '../../utils/formatters';
import axios from 'axios';

const API_BASE_URL = (import.meta as any).env.VITE_API_BASE_URL || '';

// STATUS COLOR SCHEME
const STATUS_COLORS: Record<string, string> = {
    'pending': '#ff9800',        // Orange/Yellow
    'processing': '#4caf50',     // Green
    'completed': '#2196f3',      // Blue
    'on-hold': '#ff5722',        // Dark Orange
    'cancelled': '#757575',      // Grey
    'failed': '#f44336',         // Red
    'refunded': '#9c27b0',       // Purple
};

interface OrderRow {
    id: number;
    dbId: number;
    sNo: number;
    orderNumber: string;
    date: string;
    orderStatus: string;
    orderSource: string;
    deliveryDate: string;
    deliverySlot: string;
    customerName: string;
    itemsOrdered: string;
    totalItems: number;
    shippingAddress: string;
    mobileNumber: string;
    customerNotes: string;
    orderTotal: number;
    paymentMethod: string;
    transactionId: string;
    isDownloaded: boolean;
    isZohoExported: boolean;
    zohoInvoiceNo: string;
}

export default function B2COrderList() {
    const { enqueueSnackbar } = useSnackbar();
    const apiRef = useGridApiRef();

    // Calculate last 2 days for default filter
    const getTwoDaysAgo = () => {
        const date = new Date();
        date.setDate(date.getDate() - 2);
        return date.toISOString().split('T')[0];
    };

    const getToday = () => {
        return new Date().toISOString().split('T')[0];
    };

    // State
    const [startDate, setStartDate] = useState(getTwoDaysAgo());
    const [endDate, setEndDate] = useState(getToday());
    const [orderStatus, setOrderStatus] = useState<string[]>([]);
    const [orders, setOrders] = useState<OrderRow[]>([]);
    const [selectedOrders, setSelectedOrders] = useState<GridRowSelectionModel>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [syncProgress, setSyncProgress] = useState(0);

    // Order detail popup state
    const [selectedOrderDetail, setSelectedOrderDetail] = useState<any>(null);

    // Custom sync dialog state
    const [customSyncOpen, setCustomSyncOpen] = useState(false);
    const [customSyncStart, setCustomSyncStart] = useState(getTwoDaysAgo());
    const [customSyncEnd, setCustomSyncEnd] = useState(getToday());
    const [orderDetailOpen, setOrderDetailOpen] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // Global oldest order (independent of filter)
    const [oldestOrder, setOldestOrder] = useState<{ number: string, date: string } | null>(null);

    // Load latest orders from database on mount
    useEffect(() => {
        loadOrdersFromDatabase();
        fetchOldestOrder();
    }, []);

    // Transform database order for DataGrid format
    const transformDatabaseOrder = (order: any, idx: number): OrderRow => {
        const billing = typeof order.billing === 'string' ? JSON.parse(order.billing) : order.billing;
        const shipping = typeof order.shipping === 'string' ? JSON.parse(order.shipping) : order.shipping;
        const lineItems = order.line_items || [];

        const itemsOrdered = lineItems
            .map((item: any) => `${item.name} x ${item.quantity}`)
            .join(', ');

        const totalItems = order.total_items || lineItems.reduce((sum: number, item: any) => sum + item.quantity, 0);

        const shippingAddress = [
            shipping.address_1,
            shipping.address_2,
            shipping.city,
            shipping.state,
            shipping.postcode,
            shipping.country
        ].filter(Boolean).join(', ');

        const customerName = `${billing.first_name || ''} ${billing.last_name || ''}`.trim() || 'N/A';

        return {
            id: order.woo_order_id,
            dbId: order.id,
            sNo: idx + 1,
            orderNumber: order.order_number,
            date: formatDate(order.date_created),
            orderStatus: order.status,
            orderSource: order.order_source || 'website',
            deliveryDate: order.delivery_date || '-',
            deliverySlot: order.delivery_slot_id || '-',
            customerName,
            itemsOrdered: itemsOrdered || 'N/A',
            totalItems,
            shippingAddress: shippingAddress || 'N/A',
            mobileNumber: billing.phone || '',
            customerNotes: order.customer_note || '-',
            orderTotal: parseFloat(order.total),
            paymentMethod: order.payment_method_title || order.payment_method || '-',
            transactionId: order.transaction_id || '-',
            isDownloaded: !!order.is_downloaded_for_packaging,
            isZohoExported: !!order.is_exported_to_zoho,
            zohoInvoiceNo: order.zoho_invoice_number || '-',
        };
    };

    // Calculate total items across all orders
    const calculateTotalItems = (): number => {
        return orders.reduce((sum, order) => sum + order.totalItems, 0);
    };

    // Load orders from database

    // Fetch global oldest order (independent of filter)
    const fetchOldestOrder = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/v1/b2c-orders/oldest`,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
                }
            );

            if (response.data) {
                setOldestOrder({
                    number: response.data.order_number,
                    date: formatDate(response.data.date_created)
                });
            }
        } catch (error) {
            console.error('Error fetching oldest order:', error);
        }
    };
    const loadOrdersFromDatabase = async () => {
        try {
            // Build query params with date range
            const params: any = { page: 1, limit: 5000 };

            if (startDate) {
                // Convert to midnight local time, then to ISO
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                params.start_date = start.toISOString();
            }

            if (endDate) {
                // Convert to end of day local time, then to ISO
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                params.end_date = end.toISOString();
            }

            const response = await axios.get(
                `${API_BASE_URL}/api/v1/b2c-orders`,
                {
                    params,
                    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
                }
            );

            const transformedOrders = response.data.orders.map(transformDatabaseOrder);
            setOrders(transformedOrders);
            setSelectedOrders([]);
        } catch (error: any) {
            console.error('Error loading orders from database:', error);
        }
    };

    // Search orders (database-only)
    const handleSearchOrders = async () => {
        setLoading(true);

        try {
            // Build query params
            const params: any = { page: 1, limit: 100 };
            if (orderStatus.length > 0) {
                params.status = orderStatus.join(',');
            }

            if (startDate) {
                // Convert to midnight local time, then to ISO
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                params.start_date = start.toISOString();
            }

            if (endDate) {
                // Convert to end of day local time, then to ISO
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                params.end_date = end.toISOString();
            }

            const response = await axios.get(
                `${API_BASE_URL}/api/v1/b2c-orders`,
                {
                    params,
                    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
                }
            );

            const transformedOrders = response.data.orders.map(transformDatabaseOrder);
            setOrders(transformedOrders);
            setSelectedOrders([]);

            enqueueSnackbar(`Found ${transformedOrders.length} orders`, { variant: 'success' });
        } catch (error: any) {
            console.error('Error searching orders:', error);
            enqueueSnackbar('Failed to search orders', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Sync with WooCommerce (last 2 days)
    const handleSync = async () => {
        setSyncing(true);
        setSyncProgress(0);

        // Simulate progress (since we don't have real-time progress from backend)
        const progressInterval = setInterval(() => {
            setSyncProgress(prev => Math.min(prev + 10, 90));
        }, 500);

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/v1/b2c-orders/sync-latest`,
                {},
                { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
            );

            clearInterval(progressInterval);
            setSyncProgress(100);

            enqueueSnackbar(
                `✅ Synced last 2 days: ${response.data.synced} orders (${response.data.created} new, ${response.data.updated} updated)`,
                { variant: 'success' }
            );

            // Auto-refresh table
            await loadOrdersFromDatabase();

            // Reset progress after a short delay
            setTimeout(() => setSyncProgress(0), 1000);
        } catch (error: any) {
            clearInterval(progressInterval);
            setSyncProgress(0);
            console.error('Error syncing orders:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Failed to sync orders',
                { variant: 'error' }
            );
        } finally {
            setSyncing(false);
        }
    };

    // Custom date range sync
    const handleCustomSync = async () => {
        // Validate dates
        const start = new Date(customSyncStart);
        const end = new Date(customSyncEnd);

        if (start > end) {
            enqueueSnackbar('Start date must be before end date', { variant: 'error' });
            return;
        }

        const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        if (daysDiff > 90) {
            enqueueSnackbar(`Date range too large (${daysDiff} days). Maximum is 90 days`, { variant: 'error' });
            return;
        }

        // Keep dialog open during sync (don't close here)
        setSyncing(true);
        setSyncProgress(0);

        const progressInterval = setInterval(() => {
            setSyncProgress(prev => Math.min(prev + 10, 90));
        }, 500);

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/v1/b2c-orders/sync-custom`,
                null,
                {
                    params: {
                        start_date: customSyncStart,
                        end_date: customSyncEnd
                    },
                    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
                }
            );

            // Print thermal receipt
            const handlePrintReceipt = async (dbId: number, orderNumber: string) => {
                try {
                    const response = await axios.get(
                        `${API_BASE_URL}/api/v1/b2c-orders/${dbId}/thermal-receipt`,
                        {
                            responseType: 'blob',
                            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
                        }
                    );

                    // Download PDF
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `receipt_${orderNumber}.pdf`;
                    link.click();
                    window.URL.revokeObjectURL(url);

                    enqueueSnackbar(`Receipt downloaded for order #${orderNumber}`, { variant: 'success' });
                } catch (error: any) {
                    console.error('Error downloading receipt:', error);
                    enqueueSnackbar(
                        error.response?.data?.detail || 'Failed to download receipt',
                        { variant: 'error' }
                    );
                }
            };

            clearInterval(progressInterval);
            setSyncProgress(100);

            enqueueSnackbar(
                `✅ ${response.data.message} (${response.data.created} new, ${response.data.updated} updated)`,
                { variant: 'success' }
            );

            await loadOrdersFromDatabase();
            setTimeout(() => setSyncProgress(0), 1000);

            // Close dialog only after successful sync
            setCustomSyncOpen(false);
        } catch (error: any) {
            clearInterval(progressInterval);
            setSyncProgress(0);
            console.error('Error syncing orders:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Failed to sync orders',
                { variant: 'error' }
            );
        } finally {
            setSyncing(false);
        }
    };


    // Print thermal receipt
    const handlePrintReceipt = async (dbId: number, orderNumber: string) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/v1/b2c-orders/${dbId}/thermal-receipt`,
                {
                    responseType: 'blob',
                    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
                }
            );

            // Download PDF
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `receipt_${orderNumber}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);

            enqueueSnackbar(`Receipt downloaded for order #${orderNumber}`, { variant: 'success' });
        } catch (error: any) {
            console.error('Error downloading receipt:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Failed to download receipt',
                { variant: 'error' }
            );
        }
    };
    // View order details
    const handleViewOrderDetail = async (orderNumber: string) => {
        setLoadingDetail(true);
        setOrderDetailOpen(true);

        try {
            // Find the order ID from our current orders list
            const order = orders.find(o => o.orderNumber === orderNumber);
            if (!order) return;

            const response = await axios.get(
                `${API_BASE_URL}/api/v1/b2c-orders/${order.dbId}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
            );

            setSelectedOrderDetail(response.data);
        } catch (error: any) {
            console.error('Error fetching order details:', error);
            enqueueSnackbar('Failed to load order details', { variant: 'error' });
            setOrderDetailOpen(false);
        } finally {
            setLoadingDetail(false);
        }
    };

    // Export orders
    const handleExportOrders = async () => {
        if (selectedOrders.length === 0) {
            enqueueSnackbar('Please select at least one order to export', { variant: 'warning' });
            return;
        }

        setExporting(true);
        try {
            const blob = await b2cOpsAPI.exportOrders(selectedOrders as any[], '', '');

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `orders_${new Date().getTime()}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            enqueueSnackbar(`Successfully exported ${selectedOrders.length} orders!`, { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to export orders', { variant: 'error' });
        } finally {
            setExporting(false);
        }
    };

    // DataGrid columns with status color and clickable order number
    const columns: GridColDef[] = [
        { field: 'sNo', headerName: 'S.No', width: 70 },
        {
            field: 'isDownloaded',
            headerName: 'Pkg',
            width: 60,
            renderCell: (params) => (
                <InventoryIcon
                    color={params.value ? 'success' : 'disabled'}
                    titleAccess={params.value ? 'Downloaded for Packaging' : 'Not Downloaded'}
                />
            )
        },
        {
            field: 'isZohoExported',
            headerName: 'Zoho',
            width: 60,
            renderCell: (params) => (
                params.value ?
                    <CloudDoneIcon color="success" titleAccess="Exported to Zoho" /> :
                    <CloudOffIcon color="disabled" titleAccess="Not Exported" />
            )
        },
        { field: 'zohoInvoiceNo', headerName: 'Zoho Inv #', width: 120 },
        {
            field: 'orderNumber',
            headerName: 'Order #',
            width: 100,
            renderCell: (params) => (
                <Typography
                    sx={{
                        color: 'primary.main',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': { color: 'primary.dark' }
                    }}
                    onClick={() => handleViewOrderDetail(params.value as string)}
                >
                    {params.value}
                </Typography>
            )
        },
        { field: 'date', headerName: 'Date', width: 110 },
        {
            field: 'orderStatus',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => {
                const status = params.value as string;
                return (
                    <Chip
                        label={status.toUpperCase()}
                        size="small"
                        sx={{
                            bgcolor: STATUS_COLORS[status] || '#757575',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                        }}
                    />
                );
            }
        },
        {
            field: 'orderSource',
            headerName: 'Source',
            width: 110,
            renderCell: (params) => {
                const source = params.value as string;
                const colors: Record<string, string> = {
                    'website': '#2196f3',
                    'mkterp': '#9c27b0',
                    'mobile': '#ff9800',
                    'whatsapp': '#4caf50',
                    'admin': '#f44336'
                };
                return (
                    <Chip
                        label={source.toUpperCase()}
                        size="small"
                        sx={{
                            bgcolor: colors[source] || '#757575',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                        }}
                    />
                );
            }
        },
        { field: 'deliveryDate', headerName: 'Delivery Date', width: 120 },
        { field: 'deliverySlot', headerName: 'Slot', width: 80 },
        { field: 'customerName', headerName: 'Name', width: 150 },
        { field: 'itemsOrdered', headerName: 'Items Ordered', width: 250 },
        { field: 'totalItems', headerName: 'Total Items', width: 100 },
        { field: 'shippingAddress', headerName: 'Shipping Address', width: 300 },
        { field: 'mobileNumber', headerName: 'Mobile', width: 130 },
        { field: 'customerNotes', headerName: 'Notes', width: 200 },
        { field: 'orderTotal', headerName: 'Total', width: 100, type: 'number' },
        { field: 'paymentMethod', headerName: 'Payment Method', width: 150 },
        { field: 'transactionId', headerName: 'Transaction ID', width: 150 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handlePrintReceipt(params.row.dbId, params.row.orderNumber)}
                    title="Print Receipt"
                >
                    <PrintIcon />
                </IconButton>
            ),
        },
    ];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" gutterBottom fontWeight="bold">
                            📦 B2C Order List
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Database-first order management with independent sync
                        </Typography>
                    </Box>
                    <Chip
                        icon={<CheckCircle />}
                        label="🔔 Webhook Sync Active"
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 'bold' }}
                    />
                </Box>
            </Box>

            {/* Search & Sync Controls */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    🔍 Search & Sync
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                    />
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Order Status</InputLabel>
                        <Select
                            multiple
                            value={orderStatus}
                            label="Order Status"
                            onChange={(e) => setOrderStatus(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                            renderValue={(selected) =>
                                selected.length === 0
                                    ? 'Any Status'
                                    : selected.map(s => s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')).join(', ')
                            }
                        >
                            <MenuItem value="processing">Processing</MenuItem>
                            <MenuItem value="pending">Pending Payment</MenuItem>
                            <MenuItem value="on-hold">On Hold</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                        onClick={handleSearchOrders}
                        disabled={loading || syncing}
                        sx={{ minWidth: 150 }}
                    >
                        {loading ? 'Filtering...' : 'Filter by Status'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        onClick={handleSync}
                        disabled={loading || syncing}
                        sx={{ minWidth: 180 }}
                    >
                        {syncing ? 'Syncing...' : 'Sync (Last 2 Days)'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<CalendarTodayIcon />}
                        onClick={() => setCustomSyncOpen(true)}
                        disabled={loading || syncing}
                        sx={{ minWidth: 150 }}
                    >
                        Custom Sync
                    </Button>
                </Box>

                {/* Sync Progress Bar */}
                {syncing && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <LinearProgress variant="determinate" value={syncProgress} />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Syncing orders from WooCommerce... {syncProgress}%
                        </Typography>
                    </Box>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>Search by date range and status</strong> to filter database orders. <strong>Sync</strong> fetches last 2 days from WooCommerce. Default view shows last 2 days.
                </Alert>
            </Paper>

            {/* Orders Table */}
            {orders.length > 0 && (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="h6">
                                📊 Total Orders Found: {orders.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Displaying orders between <strong>{new Date(startDate).toLocaleDateString('en-GB')}</strong> and <strong>{new Date(endDate).toLocaleDateString('en-GB')}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Items: <strong>{calculateTotalItems()}</strong> | Total Revenue: <strong>₹{orders.reduce((sum, o) => sum + o.orderTotal, 0).toFixed(2)}</strong>
                            </Typography>
                            {oldestOrder && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Oldest Order in Database: <strong>#{oldestOrder.number}</strong> on <strong>{oldestOrder.date}</strong>
                                </Typography>
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                            onClick={handleExportOrders}
                            disabled={exporting || selectedOrders.length === 0}
                        >
                            {exporting ? 'Exporting...' : `Download Selected (${selectedOrders.length})`}
                        </Button>
                    </Box>

                    <Box
                        className={isFullscreen ? 'datagrid-fullscreen' : ''}
                        sx={{
                            height: isFullscreen ? '100vh' : 600,
                            width: '100%',
                            transition: 'height 0.3s ease',
                        }}
                    >
                        <DataGrid
                            apiRef={apiRef}
                            rows={orders}
                            columns={columns}
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={selectedOrders}
                            onRowSelectionModelChange={(newSelection) => setSelectedOrders(newSelection)}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            slots={{
                                toolbar: () => (
                                    <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Button
                                            startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            size="small"
                                        >
                                            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                        </Button>
                                    </Box>
                                ),
                            }}
                            sx={{
                                border: '1px solid #e0e0e0',
                                height: '100%',
                                '& .MuiDataGrid-cell': {
                                    borderRight: '1px solid #e0e0e0',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '2px solid #e0e0e0',
                                    backgroundColor: '#fafafa',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    borderRight: '1px solid #e0e0e0',
                                },
                            }}
                        />
                    </Box>
                </Paper>
            )}

            {/* Order Detail Dialog */}
            <Dialog
                open={orderDetailOpen}
                onClose={() => setOrderDetailOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Order Details</Typography>
                    <IconButton onClick={() => setOrderDetailOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {loadingDetail ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : selectedOrderDetail ? (
                        <Box>
                            {/* Order Info */}
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Order Information
                            </Typography>
                            <Table size="small" sx={{ mb: 2 }}>
                                <TableBody>
                                    <TableRow>
                                        <TableCell><strong>Order #:</strong></TableCell>
                                        <TableCell>{selectedOrderDetail.order_number}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Date:</strong></TableCell>
                                        <TableCell>{formatDate(selectedOrderDetail.date_created)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Status:</strong></TableCell>
                                        <TableCell>
                                            <Chip
                                                label={selectedOrderDetail.status?.toUpperCase()}
                                                size="small"
                                                sx={{
                                                    bgcolor: STATUS_COLORS[selectedOrderDetail.status] || '#757575',
                                                    color: 'white'
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Total:</strong></TableCell>
                                        <TableCell>₹{selectedOrderDetail.total}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {/* Customer Info */}
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Customer Information
                            </Typography>
                            <Table size="small" sx={{ mb: 2 }}>
                                <TableBody>
                                    <TableRow>
                                        <TableCell><strong>Name:</strong></TableCell>
                                        <TableCell>
                                            {selectedOrderDetail.billing?.first_name} {selectedOrderDetail.billing?.last_name}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Email:</strong></TableCell>
                                        <TableCell>{selectedOrderDetail.billing?.email}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Phone:</strong></TableCell>
                                        <TableCell>{selectedOrderDetail.billing?.phone}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {/* Line Items */}
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Items Ordered
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Product</strong></TableCell>
                                        <TableCell align="center"><strong>Qty</strong></TableCell>
                                        <TableCell align="right"><strong>Price</strong></TableCell>
                                        <TableCell align="right"><strong>Total</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedOrderDetail.line_items?.map((item: any, idx: number) => (
                                        <TableRow key={idx}>
                                            <TableCell>{item.name}</TableCell>
                                            <TableCell align="center">{item.quantity}</TableCell>
                                            <TableCell align="right">₹{item.price}</TableCell>
                                            <TableCell align="right">₹{item.total}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOrderDetailOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Custom Sync Dialog */}
            <Dialog open={customSyncOpen} onClose={syncing ? undefined : () => setCustomSyncOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Custom Date Range Sync</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={customSyncStart}
                            onChange={(e) => setCustomSyncStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={syncing}
                            fullWidth
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            value={customSyncEnd}
                            onChange={(e) => setCustomSyncEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={syncing}
                            fullWidth
                        />
                        <Alert severity="info">
                            Maximum date range: 90 days
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCustomSyncOpen(false)} disabled={syncing}>Cancel</Button>
                    <Button
                        onClick={handleCustomSync}
                        variant="contained"
                        color="primary"
                        disabled={syncing}
                        startIcon={syncing ? <CircularProgress size={20} /> : null}
                    >
                        {syncing ? 'Syncing...' : 'Sync Orders'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
